import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, User, Calendar, Megaphone, Plus, StickyNote, CheckSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PortalLayout, SectionTitle, Panel, EmptyState } from "@/components/platform/PortalLayout";

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 focus:border-white outline-none px-3 py-2.5 text-sm transition-colors";

export default function CoachDashboard() {
  const [roster, setRoster] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [notes, setNotes] = useState([]);
  const [evals, setEvals] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [evalForm, setEvalForm] = useState({ title: "", summary: "" });
  const [testForm, setTestForm] = useState({ record_type: "retest", ten_yard: "", flying_ten: "", vertical: "", broad: "" });
  const [annForm, setAnnForm] = useState({ title: "", body: "" });

  const loadBase = useCallback(async () => {
    try {
      const [{ data: aths }, { data: sess }] = await Promise.all([api.get("/athletes"), api.get("/sessions")]);
      setRoster(aths); setSessions(sess);
      setSelectedId((prev) => prev || (aths[0]?.id ?? null));
    } catch (e) { toast.error("Failed to load roster."); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  const loadAthleteData = useCallback(async (id) => {
    if (!id) return;
    try {
      const [{ data: n }, { data: ev }, { data: att }] = await Promise.all([
        api.get(`/coach-notes?athlete_id=${id}`),
        api.get(`/evaluations?athlete_id=${id}`),
        api.get(`/attendance?athlete_id=${id}`),
      ]);
      setNotes(n); setEvals(ev); setAttendance(att);
    } catch (e) { toast.error("Failed to load athlete details."); }
  }, []);

  useEffect(() => { loadAthleteData(selectedId); }, [selectedId, loadAthleteData]);

  const selected = roster.find((a) => a.id === selectedId);
  const today = new Date().toISOString();

  const markAttendance = async (status) => {
    try {
      await api.post("/attendance", { athlete_id: selectedId, date: today, status });
      toast.success(`Marked ${status}`);
      loadAthleteData(selectedId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try {
      await api.post("/coach-notes", { athlete_id: selectedId, note: noteText });
      setNoteText(""); toast.success("Note added"); loadAthleteData(selectedId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addEval = async (e) => {
    e.preventDefault();
    try {
      await api.post("/evaluations", { athlete_id: selectedId, title: evalForm.title, summary: evalForm.summary, date: today });
      setEvalForm({ title: "", summary: "" }); toast.success("Evaluation saved"); loadAthleteData(selectedId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const postAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", { title: annForm.title, body: annForm.body });
      setAnnForm({ title: "", body: "" }); toast.success("Announcement posted to your athletes");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addTest = async (e) => {
    e.preventDefault();
    const num = (v) => (v === "" ? null : parseFloat(v));
    try {
      await api.post("/test-records", {
        athlete_id: selectedId, record_type: testForm.record_type,
        ten_yard: num(testForm.ten_yard), flying_ten: num(testForm.flying_ten),
        vertical: num(testForm.vertical), broad: num(testForm.broad),
      });
      setTestForm({ record_type: "retest", ten_yard: "", flying_ten: "", vertical: "", broad: "" });
      toast.success("Test recorded — scores auto-calculated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) {
    return <PortalLayout roleLabel="Coach Portal"><div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div></PortalLayout>;
  }

  return (
    <PortalLayout roleLabel="Coach Portal">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-8">Coach Dashboard</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Roster */}
        <div>
          <SectionTitle>Assigned Roster ({roster.length})</SectionTitle>
          {roster.length ? (
            <div className="space-y-2" data-testid="coach-roster">
              {roster.map((a) => (
                <button key={a.id} data-testid={`coach-athlete-${a.id}`} onClick={() => setSelectedId(a.id)}
                  className={`w-full text-left p-4 border transition-colors ${a.id === selectedId ? "border-white bg-white/5" : "border-white/10 hover:border-white/25"}`}>
                  <p className="font-bold uppercase tracking-tight">{a.name}</p>
                  <p className="text-white/40 text-xs">{[a.sport, a.position].filter(Boolean).join(" · ") || "—"}</p>
                </button>
              ))}
            </div>
          ) : <EmptyState testid="coach-no-roster">No athletes assigned yet. Ask the admin to assign athletes to you.</EmptyState>}

          <div className="mt-8">
            <SectionTitle>Upcoming Sessions</SectionTitle>
            {sessions.length ? sessions.map((s) => (
              <Panel key={s.id} className="mb-3"><div className="flex items-center gap-2 mb-1"><Calendar size={15} className="text-white/60" /><p className="font-bold">{s.title}</p></div><p className="text-white/50 text-sm">{new Date(s.start_time).toLocaleString()}</p></Panel>
            )) : <EmptyState testid="coach-no-sessions">No sessions scheduled.</EmptyState>}
          </div>

          <div className="mt-8">
            <SectionTitle>Post Announcement</SectionTitle>
            <form onSubmit={postAnnouncement} className="space-y-3" data-testid="coach-announce-form">
              <input required placeholder="Title" data-testid="coach-ann-title" value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} className={inputCls} />
              <textarea required placeholder="Message to your athletes" data-testid="coach-ann-body" value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} className={inputCls} rows={3} />
              <button data-testid="coach-ann-submit" className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><Megaphone size={14} /> Post</button>
            </form>
          </div>
        </div>

        {/* Selected athlete management */}
        <div className="lg:col-span-2 space-y-6">
          {!selected ? <EmptyState testid="coach-select-hint">Select an athlete to manage.</EmptyState> : (
            <div data-testid="coach-athlete-panel">
              <Panel className="mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center"><User size={22} /></div>
                  <div><p className="font-display font-bold uppercase text-2xl tracking-tight">{selected.name}</p>
                    <p className="text-white/50 text-sm">{[selected.sport, selected.position, selected.school].filter(Boolean).join(" · ") || "—"}</p></div>
                </div>
              </Panel>

              <SectionTitle>Attendance</SectionTitle>
              <Panel className="mb-6">
                <div className="flex gap-2 flex-wrap mb-4">
                  {["present", "absent", "late", "excused"].map((s) => (
                    <button key={s} data-testid={`coach-mark-${s}`} onClick={() => markAttendance(s)} className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><CheckSquare size={13} /> {s}</button>
                  ))}
                </div>
                {attendance.length ? (
                  <div className="space-y-1.5">{attendance.slice(0, 5).map((r) => (
                    <div key={r.id} className="flex justify-between text-sm border-b border-white/5 pb-1.5 last:border-0"><span className="text-white/60">{new Date(r.date).toLocaleDateString()}</span><span className="uppercase tracking-widest text-xs font-bold">{r.status}</span></div>
                  ))}</div>
                ) : <p className="text-white/40 text-sm">No attendance recorded.</p>}
              </Panel>

              <SectionTitle>Evaluations</SectionTitle>
              <Panel className="mb-6">
                <form onSubmit={addEval} className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="coach-eval-form">
                  <input required placeholder="Title" data-testid="coach-eval-title" value={evalForm.title} onChange={(e) => setEvalForm({ ...evalForm, title: e.target.value })} className={inputCls} />
                  <input placeholder="Summary" data-testid="coach-eval-summary" value={evalForm.summary} onChange={(e) => setEvalForm({ ...evalForm, summary: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                  <button data-testid="coach-eval-submit" className="sm:col-span-3 justify-self-start inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><Plus size={14} /> Add Evaluation</button>
                </form>
                {evals.length ? evals.map((ev) => (
                  <div key={ev.id} className="border-t border-white/5 pt-3 mt-3 first:border-0 first:mt-0 first:pt-0"><p className="font-bold text-sm">{ev.title}</p>{ev.summary && <p className="text-white/60 text-sm">{ev.summary}</p>}</div>
                )) : <p className="text-white/40 text-sm">No evaluations yet.</p>}
              </Panel>

              <SectionTitle>Record Test</SectionTitle>
              <Panel className="mb-6">
                <form onSubmit={addTest} className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="coach-test-form">
                  <select data-testid="test-type" value={testForm.record_type} onChange={(e) => setTestForm({ ...testForm, record_type: e.target.value })} className={inputCls}>
                    {["evaluation", "retest", "testing"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input data-testid="test-ten" placeholder="10yd (s)" value={testForm.ten_yard} onChange={(e) => setTestForm({ ...testForm, ten_yard: e.target.value })} className={inputCls} />
                  <input data-testid="test-fly" placeholder="Flying 10 (s)" value={testForm.flying_ten} onChange={(e) => setTestForm({ ...testForm, flying_ten: e.target.value })} className={inputCls} />
                  <input data-testid="test-vert" placeholder="Vertical (in)" value={testForm.vertical} onChange={(e) => setTestForm({ ...testForm, vertical: e.target.value })} className={inputCls} />
                  <input data-testid="test-broad" placeholder="Broad (in)" value={testForm.broad} onChange={(e) => setTestForm({ ...testForm, broad: e.target.value })} className={inputCls} />
                  <button data-testid="coach-test-submit" className="col-span-2 sm:col-span-5 justify-self-start inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><Plus size={14} /> Save Test (scores auto-calc)</button>
                </form>
              </Panel>

              <SectionTitle>Coach Notes (private)</SectionTitle>
              <Panel>
                <form onSubmit={addNote} className="flex gap-2 mb-4" data-testid="coach-note-form">
                  <input placeholder="Add a private note..." data-testid="coach-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} className={inputCls} />
                  <button data-testid="coach-note-submit" className="inline-flex items-center gap-2 border border-white/20 px-4 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors"><StickyNote size={14} /></button>
                </form>
                {notes.length ? notes.map((n) => (
                  <div key={n.id} className="border-t border-white/5 pt-2 mt-2 first:border-0 first:mt-0 first:pt-0"><p className="text-white/70 text-sm">{n.note}</p><p className="text-white/30 text-[10px] mt-0.5">{n.author_name} · {new Date(n.created_at).toLocaleDateString()}</p></div>
                )) : <p className="text-white/40 text-sm">No notes yet.</p>}
              </Panel>
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
